import * as _ from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import EmptyState from '../../components/EmptyState';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../../components/LoadingIndicator';
import { gql } from '../../graphql/generated/gql';
import ConnectionHelper from '../../utils/graphql/ConnectionHelper';
import Link from '../../utils/router/Link';
import * as styles from './ExtensionPostList.module.scss';
import ExtensionSearchBar from './ExtensionSearchBar';
import ExtensionSearchHighlight from './ExtensionSearchHighlight';
import ExtensionPostRow from './post/ExtensionPostRow';
import sendMessageToSlabFrame from './sendMessageToSlabFrame';

const VIEWED_POSTS_QUERY = gql(/** @complexity 40 */ `
  query ExtensionViewedPostList($afterCursor: String) {
    listPosts(first: 25, after: $afterCursor, filters: [VIEWER], sortBy: VIEWED) {
      edges {
        node {
          id
          ...ExtensionPostRow_post
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const ViewedPostList = () => {
  const viewedPostsResult = useQuery(VIEWED_POSTS_QUERY);
  const fetchMore = ConnectionHelper.generateFetchMore(
    VIEWED_POSTS_QUERY,
    viewedPostsResult,
    'listPosts',
  );
  const posts = _.compact(
    viewedPostsResult.data?.listPosts?.edges?.map((edge) => edge?.node),
  );

  const hasData = !viewedPostsResult.loading || !!viewedPostsResult.data;
  useEffect(() => {
    if (hasData) {
      // Wait for the next paint to ensure smooth transition from skeleton loader
      // to actual content in the extension UI
      requestAnimationFrame(() => {
        sendMessageToSlabFrame({ 'slab.type': 'ready' });
      });
    }
  }, [hasData]);

  let content = null;
  if (posts) {
    content = posts.map((post) => (
      <ExtensionPostRow key={post.id} post={post} />
    ));
  } else if (viewedPostsResult.loading) {
    content = (
      <div className={styles.loading}>
        <LoadingIndicator />
      </div>
    );
  } else if (viewedPostsResult.error) {
    content = (
      <div>
        <h3>Oops! Something went wrong.</h3>
        <p>
          Please try again later or{' '}
          <Link to="https://go.slab.com/slab-contact-us">contact us</Link> if
          the problem persists.
        </p>
      </div>
    );
  }

  return (
    <>
      {content}
      {fetchMore && <InfiniteScrollWaypoint fetchMore={fetchMore} />}
    </>
  );
};

const SEARCH_QUERY = gql(/** @complexity 22 */ `
  query ExtensionSearchResultList(
    $query: String!
    $first: Int!
    $afterCursor: String
  ) {
    search(query: $query, first: $first, after: $afterCursor) {
      edges {
        node {
          ... on PostSearchResult {
            title
            post {
              id
              ...ExtensionPostRow_post
            }
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

interface SearchResultsProps {
  keyword: string;
}

const SearchResultList = ({ keyword }: SearchResultsProps) => {
  const { data, loading } = useQuery(SEARCH_QUERY, {
    variables: {
      query: keyword,
      first: 100,
    },
  });

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingIndicator />
      </div>
    );
  }

  const nodes = _.compact(
    data?.search?.edges?.map((edge) => edge?.node),
  ).filter((node) => 'post' in node && node.post);

  if (!nodes?.length) {
    return (
      <EmptyState
        imageName="notFound"
        title="No results found"
        message="Try another search with fewer keywords"
      />
    );
  }

  return (
    <>
      {nodes.map((node) =>
        'post' in node ? (
          <ExtensionPostRow
            key={node.post.id}
            post={node.post}
            title={
              <ExtensionSearchHighlight text={node.title ?? node.post.title} />
            }
          />
        ) : null,
      )}
    </>
  );
};

const ExtensionPostList = () => {
  const [isStuck, setIsStuck] = useState(false);
  const [keyword, setKeyword] = useState<string | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.container}>
      <ExtensionSearchBar
        stuck={isStuck}
        onClose={() => {
          sendMessageToSlabFrame({ 'slab.type': 'sidebar.close' });
          sendMessageToSlabFrame({ 'slab.type': 'hovercard.close' });
        }}
        onMouseEnter={() => {
          sendMessageToSlabFrame({ 'slab.type': 'hovercard.close' });
        }}
        onSubmit={(newKeyword) => {
          setKeyword(newKeyword);
        }}
      />
      <div
        ref={ref}
        className={styles.scrollingContainer}
        onScroll={() => {
          setIsStuck((ref.current?.scrollTop ?? 0) > 0);
          sendMessageToSlabFrame({ 'slab.type': 'hovercard.close' });
        }}
      >
        {keyword ? <SearchResultList keyword={keyword} /> : <ViewedPostList />}
      </div>
    </div>
  );
};

export default ExtensionPostList;
